import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from "react";
import type { Me } from "../api/types";
import { reloadNow } from "../fresh";
import { NEWS, NEWS_VER, useNewsFresh } from "../news";
import AccountMenu from "./AccountMenu";
import AiOrb from "./AiOrb";
import { ShellIcon, type ShellIconName } from "./ShellIcons";

/** One page in the menu. */
export interface SideItem {
  key: string;
  label: string;
  icon: ShellIconName | "ai";
  /** Something waiting there. */
  badge?: number;
  /** A customer is waiting: the number is red and breathes. */
  hot?: boolean;
  /** What the number is, said in full (the folded menu's label, and for a screen reader). */
  tip?: string;
  /** 小爱's line: on duty and how fast, or what is wrong. */
  sub?: string;
  /** 小爱's connection: slow or on the stand-in (warn), not answering (bad). */
  tone?: "" | "warn" | "bad";
}

export interface SideFold {
  folded: boolean;
  /** Too narrow for the open menu (≤1099 px): icons only, no handle. */
  narrow: boolean;
  /**
   * The menu folds or opens because the page changed (to and from 对话): at once,
   * with the page, not on the spring. A new page replaces the old one in that
   * frame anyway, and sliding it meant painting the whole page as a layer of its
   * own first — on the owner's screen that put a 50–80 ms frame on every switch
   * to 对话 (measured 10/23; before the menu, 17–50 ms).
   */
  snap: boolean;
  toggle: () => void;
}

/** The sidebar's two widths; styles.css .app --sn-w holds the same. */
export const SIDE_OPEN = 224;
export const SIDE_DOCK = 64;

const FOLD_KEY = "lazior_side_folded";

/**
 * Folded or open (prototype v2.20 useFold): folded by choice (the handle, or
 * Ctrl+B / ⌘B), by itself on 对话 — where the conversation wants the width — and
 * always on a screen too narrow for it. Opened again by hand on 对话, it stays
 * open until you leave 对话.
 */
export function useSideFold(view: string): SideFold {
  const [pref, setPref] = useState(() => {
    try {
      return localStorage.getItem(FOLD_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [openOnChat, setOpenOnChat] = useState(false);
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 1099px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1099px)");
    const f = () => setNarrow(mq.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  useEffect(() => {
    if (view !== "leads") setOpenOnChat(false);
  }, [view]);
  const onChat = view === "leads";
  const folded = narrow || pref || (onChat && !openOnChat);
  const save = (v: boolean) => {
    setPref(v);
    try {
      localStorage.setItem(FOLD_KEY, v ? "1" : "0");
    } catch {
      /* this visit only */
    }
  };
  const toggle = () => {
    if (narrow) return;
    if (folded) {
      if (onChat) setOpenOnChat(true);
      if (pref) save(false);
    } else if (onChat && !pref) setOpenOnChat(false);
    else save(true);
  };
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;
  // Ctrl+B (⌘B on a Mac), except while typing: there it is somebody's bold.
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || (e.key !== "b" && e.key !== "B")) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      toggleRef.current();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const stableToggle = useCallback(() => toggleRef.current(), []);
  // Folded or opened in the same render as a change of page: snap (see SideFold).
  const last = useRef({ folded, view });
  const snap = last.current.folded !== folded && last.current.view !== view;
  useEffect(() => {
    last.current = { folded, view };
  });
  return { folded, narrow, snap, toggle: stableToggle };
}

/**
 * The page slides with the menu as it folds and opens. The prototype grew the
 * grid's column frame by frame, which lays the whole page out again on every
 * frame — on production's 客户表, tens of thousands of elements, thirty times.
 * Here the column changes once, and the page on screen is moved back to where
 * it was and slid home on the menu's own spring: one layout, then a transform
 * the compositor runs.
 */
export function useSideSlide({ folded, narrow, snap }: SideFold): void {
  const was = useRef(folded);
  useLayoutEffect(() => {
    if (was.current === folded) return;
    was.current = folded;
    if (snap || narrow || window.matchMedia("(max-width: 800px), (prefers-reduced-motion: reduce)").matches) return;
    const page = document.querySelector<HTMLElement>(".app > .viewport:not(.hidden)");
    if (!page?.animate) return;
    const dx = folded ? SIDE_OPEN - SIDE_DOCK : SIDE_DOCK - SIDE_OPEN;
    const spring = getComputedStyle(document.documentElement).getPropertyValue("--spring").trim();
    const ms = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--spring-ms"), 10) || 520;
    try {
      page.animate([{ transform: `translateX(${dx}px)` }, { transform: "translateX(0)" }], { duration: ms, easing: spring || "ease-out" });
    } catch {
      /* an engine without linear(): the page is already where it belongs */
    }
  }, [folded, narrow, snap]);
}

type Tip = { l: string; sub?: string; hot?: boolean; y: number };

/**
 * The console's frame on a computer (prototype v2.20 Sidebar): the logo row,
 * the pages in four blocks with a thin line between them (v2.11), and at the
 * foot 更新了什么 and the account. The light-jade pill under the page on screen
 * slides to the next one on the spring; the item of a page being read turns.
 * Folded, it is a 64 px dock: icons centred, numbers on the icon's shoulder,
 * and each label slides out as glass under the pointer.
 */
export default function SideNav({
  me,
  groups,
  view,
  reading,
  fold,
  fresh,
  onPick,
  onNews,
  onSignOut,
}: {
  me: Me;
  groups: SideItem[][];
  view: string;
  /** The page on screen is on its first read. */
  reading: boolean;
  fold: SideFold;
  /** The console was updated under the open page. */
  fresh: boolean;
  onPick: (key: string) => void;
  onNews: () => void;
  onSignOut: () => void;
}) {
  const navRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const newsFresh = useNewsFresh();
  const order = groups.map((g) => g.map((i) => i.key).join(",")).join("|");

  // The pill under the page on screen, slid on the spring when the page changes. The rows are measured only when
  // they change size (the observer runs once the browser has laid out), and a page change moves the pill to the
  // row's kept place: reading a row's offsetTop at the change, even a frame later, made the browser lay out the page
  // being switched to there and then, and again after its own entrance — 对话 with a chat open paid 0.17 s + 0.18 s
  // in one frame (10/23 at commit time, 10/25 in the next frame).
  const spots = useRef(new WeakMap<Element, { top: number; h: number }>());
  const placeRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => {
      nav.querySelectorAll<HTMLElement>(".sn-item").forEach((n) => spots.current.set(n, { top: n.offsetTop, h: n.offsetHeight }));
      placeRef.current();
    });
    nav.querySelectorAll(".sn-item").forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [fold.folded, order]);
  useEffect(() => {
    const nav = navRef.current;
    const pill = pillRef.current;
    if (!nav || !pill) return;
    const place = () => {
      const on = nav.querySelector<HTMLElement>(".sn-item.on");
      if (!on) {
        pill.style.opacity = "0";
        return;
      }
      const at = spots.current.get(on);
      if (!at) return; // the observer places it once the rows are laid out
      pill.style.opacity = "1";
      pill.style.height = `${at.h}px`;
      pill.style.transform = `translateY(${at.top}px)`;
      // Moving from the frame after its first placement: it does not slide in from the top.
      if (!pill.dataset.ready) requestAnimationFrame(() => (pill.dataset.ready = "1"));
    };
    placeRef.current = place;
    place();
  }, [view, fold.folded, order]);

  useEffect(() => {
    if (!fold.folded) setTip(null);
  }, [fold.folded]);
  const tipOn = (e: SyntheticEvent<HTMLElement>, t: Omit<Tip, "y">) => {
    if (!fold.folded) return;
    const b = e.currentTarget.getBoundingClientRect();
    setTip({ ...t, y: Math.round(b.top + b.height / 2) });
  };
  const tipOff = () => setTip(null);

  const mark = (
    <>
      <span className="sn-mark" aria-hidden="true">
        LZ
        {fresh && <i className="sn-mark-dot" />}
      </span>
      <span className="sn-name">
        <b>LaZior</b>
        {fresh ? (
          <small className="sn-new">
            <ShellIcon name="refresh" size={12} />
            有新版本 · 刷新
          </small>
        ) : (
          <small>销售控制台</small>
        )}
      </span>
    </>
  );

  return (
    <>
      <nav className="sn" aria-label="控制台">
        {fresh ? (
          <button
            type="button"
            className="sn-brand is-new"
            onClick={reloadNow}
            aria-label="CRM 有新版本，按一下刷新"
            onMouseEnter={(e) => tipOn(e, { l: "CRM 有新版本", sub: "按一下刷新" })}
            onMouseLeave={tipOff}
          >
            {mark}
          </button>
        ) : (
          <div className="sn-brand">{mark}</div>
        )}
        <div className="sn-nav" ref={navRef}>
          <span className="sn-pill" ref={pillRef} aria-hidden="true" />
          {groups.map((g, gi) => (
            <Fragment key={gi}>
              {gi > 0 && <i className="sn-sep" aria-hidden="true" />}
              {g.map((it) => {
                const on = view === it.key;
                const say = it.tip ?? it.sub;
                return (
                  <button
                    key={it.key}
                    type="button"
                    className={`sn-item${on ? " on" : ""}${it.icon === "ai" ? " sn-ai" : ""}`}
                    aria-current={on ? "page" : undefined}
                    aria-busy={(on && reading) || undefined}
                    aria-label={say ? `${it.label}，${say}` : it.label}
                    onClick={() => onPick(it.key)}
                    onMouseEnter={(e) => tipOn(e, { l: it.label, sub: say, hot: it.hot || it.tone === "bad" })}
                    onMouseLeave={tipOff}
                    // The keyboard's focus only: a mouse press focuses the item too, and redrawing the menu between the
                    // press and the release is how presses went unanswered (ShellIcons HTML). The pointer has its tip.
                    onFocus={(e) => e.currentTarget.matches(":focus-visible") && tipOn(e, { l: it.label, sub: say, hot: it.hot || it.tone === "bad" })}
                    onBlur={tipOff}
                  >
                    {it.icon === "ai" ? (
                      <AiOrb size={fold.folded ? 24 : 22} tone={it.tone} />
                    ) : (
                      <ShellIcon name={it.icon} className="sn-ic" />
                    )}
                    {on && reading && <i className="sn-spin" aria-hidden="true" />}
                    <span className="sn-t">
                      <span>{it.label}</span>
                      {it.sub && <small className={it.tone ? `sn-sub ${it.tone}` : "sn-sub"}>{it.sub}</small>}
                    </span>
                    {!!it.badge && (
                      <span className={`sn-badge${it.hot ? " hot" : ""}${it.badge > 99 ? " wide" : ""}`}>{it.badge > 99 ? "99+" : it.badge}</span>
                    )}
                    {it.icon === "ai" && it.sub && <span className={`sn-ping${it.tone ? ` ${it.tone}` : ""}`} aria-hidden="true" />}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
        <div className="sn-foot">
          <button
            type="button"
            className="sn-news"
            onClick={onNews}
            aria-label={`更新了什么，v${NEWS_VER}`}
            onMouseEnter={(e) => tipOn(e, { l: "更新了什么", sub: `v${NEWS_VER} · ${NEWS[0].date}` })}
            onMouseLeave={tipOff}
          >
            <span className="sn-news-ic">
              <ShellIcon name="sparkles" />
              {newsFresh && <i className="sn-news-dot" />}
            </span>
            <span className="sn-news-t">
              <b>更新了什么</b>
              <small>v{NEWS_VER}</small>
            </span>
            {newsFresh && <span className="sn-news-new">新</span>}
          </button>
          <AccountMenu me={me} onSignOut={onSignOut} />
        </div>
      </nav>
      {!fold.narrow && (
        <button
          type="button"
          className="sn-fold"
          onClick={fold.toggle}
          aria-label={fold.folded ? "展开侧边栏" : "收起侧边栏"}
          title={`${fold.folded ? "展开" : "收起"}侧边栏（Ctrl+B）`}
        >
          <ShellIcon name={fold.folded ? "right" : "left"} />
        </button>
      )}
      {tip && (
        <div className="sn-tip" style={{ top: tip.y }} role="tooltip">
          <b>{tip.l}</b>
          {tip.sub && <span className={tip.hot ? "hot" : undefined}>{tip.sub}</span>}
        </div>
      )}
    </>
  );
}
